import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const BookmarksContext = createContext(null);

export function BookmarksProvider({ children }) {
  const { user } = useAuth();
  // Set of "kind:slug" strings so isBookmarked is O(1).
  const [keys, setKeys] = useState(() => new Set());
  // Public counts (kind:slug → count). Loaded once for social-proof badges
  // without requiring login.
  const [counts, setCounts] = useState({});

  // Load the user's bookmarks whenever the auth state changes. On logout,
  // clear them so the UI doesn't show another user's hearts.
  useEffect(() => {
    if (!user) {
      setKeys(new Set());
      return;
    }
    api.get("/bookmarks")
      .then((r) => setKeys(new Set(r.data.items.map((i) => `${i.kind}:${i.slug}`))))
      .catch(() => { /* anon or expired — leave empty */ });
  }, [user?.id]);

  // Public counts — load once on mount; refresh sparingly.
  useEffect(() => {
    api.get("/bookmarks/counts")
      .then((r) => {
        const m = {};
        r.data.counts.forEach((c) => { m[`${c.kind}:${c.slug}`] = c.n; });
        setCounts(m);
      })
      .catch(() => { /* counts are nice-to-have */ });
  }, []);

  const isBookmarked = useCallback((kind, slug) => keys.has(`${kind}:${slug}`), [keys]);

  const toggle = useCallback(async (kind, slug) => {
    if (!user) return { needsAuth: true };
    const key = `${kind}:${slug}`;
    const had = keys.has(key);
    // Optimistic local update so the heart fills instantly
    setKeys((prev) => {
      const next = new Set(prev);
      had ? next.delete(key) : next.add(key);
      return next;
    });
    setCounts((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + (had ? -1 : 1)) }));
    try {
      if (had) {
        await api.delete(`/bookmarks/${kind}/${slug}`);
      } else {
        await api.post(`/bookmarks/${kind}/${slug}`);
      }
      return { ok: true, bookmarked: !had };
    } catch (err) {
      // Roll back on failure
      setKeys((prev) => {
        const next = new Set(prev);
        had ? next.add(key) : next.delete(key);
        return next;
      });
      setCounts((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + (had ? 1 : -1)) }));
      return { ok: false, error: err };
    }
  }, [keys, user]);

  const countFor = useCallback((kind, slug) => counts[`${kind}:${slug}`] || 0, [counts]);

  return (
    <BookmarksContext.Provider value={{ isBookmarked, toggle, countFor }}>
      {children}
    </BookmarksContext.Provider>
  );
}

export const useBookmarks = () => {
  const ctx = useContext(BookmarksContext);
  if (!ctx) throw new Error("useBookmarks must be used inside BookmarksProvider");
  return ctx;
};
